import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Button } from "@/ui/components/ui/button";
import { Checkbox } from "@/ui/components/ui/checkbox";
import { Label } from "@/ui/components/ui/label";
import { Cpu } from "lucide-react";
import { useTranslation } from "react-i18next";

interface DeviceSelectionProps {
  selectedLocationId: string;
  selectedDevices: string[];
  machines: any[] | undefined;
  isLoading: boolean;
  onDeviceToggle: (deviceId: string, checked: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const DeviceSelection: React.FC<DeviceSelectionProps> = ({
  selectedLocationId,
  selectedDevices,
  machines,
  isLoading,
  onDeviceToggle,
  onSelectAll,
  onDeselectAll,
}) => {
  const { t } = useTranslation("export");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Cpu className="h-5 w-5" />
          {t("deviceSelection.title")}
        </CardTitle>
        <CardDescription>{t("deviceSelection.description")}</CardDescription>
      </CardHeader>
      <CardContent>
        {!selectedLocationId ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("deviceSelection.noLocation")}
          </div>
        ) : isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("deviceSelection.loading")}
          </div>
        ) : machines?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("deviceSelection.empty")}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Select All/None buttons */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onSelectAll}
                disabled={selectedDevices.length === machines?.length}
              >
                {t("deviceSelection.selectAll")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onDeselectAll}
                disabled={selectedDevices.length === 0}
              >
                {t("deviceSelection.deselectAll")}
              </Button>
            </div>

            {/* Device list */}
            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
              {machines?.map((machine) => (
                <div key={machine.device_mxid} className="flex items-center space-x-2 p-2 border rounded">
                  <Checkbox
                    id={machine.device_mxid}
                    checked={selectedDevices.includes(machine.device_mxid)}
                    onCheckedChange={(checked) =>
                      onDeviceToggle(machine.device_mxid, !!checked)
                    }
                  />
                  <Label htmlFor={machine.device_mxid} className="flex-1 cursor-pointer">
                    <div className="font-medium">{machine.name}</div>
                    <div className="text-sm text-muted-foreground">{machine.device_mxid}</div>
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
